import { useId } from "react";
import type { KeyboardEvent, ReactNode } from "react";

/** One labelled row of the settings sheet: label (+ hint) on the left, the control on the right. */
export function SettingRow({
  label,
  hint,
  value,
  stacked,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string | undefined;
  value?: string | undefined;
  /** The control sits under the label (sliders, segmented controls with many options). */
  stacked?: boolean | undefined;
  htmlFor?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className={`hud-row${stacked ? " is-stacked" : ""}`}>
      <div className="hud-row-text">
        <label className="hud-row-label" htmlFor={htmlFor}>
          {label}
        </label>
        {value !== undefined ? <output className="hud-row-value">{value}</output> : null}
        {hint ? <span className="hud-row-hint">{hint}</span> : null}
      </div>
      <div className="hud-row-control">{children}</div>
    </div>
  );
}

export interface SegmentOption {
  id: string;
  label: string;
  testId?: string | undefined;
  pressed: boolean;
}

/** A segmented button group (one choice); the arrow keys move between the segments. */
export function Segmented({
  label,
  options,
  onPick,
  testId,
}: {
  label: string;
  options: readonly SegmentOption[];
  onPick: (id: string) => void;
  testId?: string | undefined;
}) {
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    const btns = [...e.currentTarget.querySelectorAll<HTMLButtonElement>("button")];
    const at = btns.indexOf(document.activeElement as HTMLButtonElement);
    if (at < 0) return;
    e.preventDefault();
    const next = btns[(at + (e.key === "ArrowRight" ? 1 : -1) + btns.length) % btns.length];
    next?.focus();
  };
  return (
    <div className="hud-seg" role="group" aria-label={label} data-testid={testId} onKeyDown={onKey}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          aria-pressed={o.pressed}
          data-testid={o.testId}
          onClick={() => onPick(o.id)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** An on/off switch (a checkbox with the switch role, so it is native to keyboards and readers). */
export function Switch({
  label,
  hint,
  checked,
  onChange,
  testId,
  disabled,
}: {
  label: string;
  hint?: string | undefined;
  checked: boolean;
  onChange: (on: boolean) => void;
  testId?: string | undefined;
  disabled?: boolean | undefined;
}) {
  const id = useId();
  return (
    <SettingRow label={label} hint={hint} htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        role="switch"
        className="hud-switch"
        checked={checked}
        disabled={disabled}
        data-testid={testId}
        onChange={(e) => onChange(e.target.checked)}
      />
    </SettingRow>
  );
}
